import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CrewMerchantApi } from 'corso-types';
import api from '~/api';
import {
  shippingClaimQueryKey,
  useShippingClaim,
} from '~/features/shippingClaimReview/ShippingClaimReviewProvider';
import { useStoreId } from '~/providers/StoreUserProvider';

export type ShippingClaimManualRefund = CrewMerchantApi.RequestBody<
  '/:storeId/shipping-protection/claim/:shippingClaimId/manual-refund',
  'post'
>;

export type ShippingClaimRefundShipping = CrewMerchantApi.RequestBody<
  '/:storeId/shipping-protection/claim/:shippingClaimId/refund-shipping',
  'post'
>;

export function useShippingClaimManualRefund() {
  const storeId = useStoreId();
  const shippingClaim = useShippingClaim();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ShippingClaimManualRefund) =>
      api
        .store(storeId)
        .shippingProtection.claim(`${shippingClaim.id}`)
        .manualRefund(body),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: shippingClaimQueryKey(shippingClaim.id, storeId),
      });
      // the claims list shows status
      await queryClient.invalidateQueries({ queryKey: ['shippingClaims'] });
    },
  });
}

/** Refunds only the order's shipping on Shopify, closing the claim as refunded. */
export function useShippingClaimRefundShipping() {
  const storeId = useStoreId();
  const shippingClaim = useShippingClaim();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ShippingClaimRefundShipping) =>
      api
        .store(storeId)
        .shippingProtection.claim(`${shippingClaim.id}`)
        .refundShipping(body),
    // a failure lands on the claim's timeline, so refetch either way
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: shippingClaimQueryKey(shippingClaim.id, storeId),
      });
      // the claims list shows status
      await queryClient.invalidateQueries({ queryKey: ['shippingClaims'] });
    },
  });
}

export type ShippingClaimManualReorder = CrewMerchantApi.RequestBody<
  '/:storeId/shipping-protection/claim/:shippingClaimId/manual-reorder',
  'post'
>;

/** Links an order already created on Shopify as the claim's reorder, closing the claim as reordered. */
export function useShippingClaimManualReorder() {
  const storeId = useStoreId();
  const shippingClaim = useShippingClaim();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ShippingClaimManualReorder) =>
      api
        .store(storeId)
        .shippingProtection.claim(`${shippingClaim.id}`)
        .manualReorder(body),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: shippingClaimQueryKey(shippingClaim.id, storeId),
      });
      // the claims list shows status, and the reorder has parcels to track
      await queryClient.invalidateQueries({ queryKey: ['shippingClaims'] });
      await queryClient.invalidateQueries({ queryKey: ['orderTracking'] });
    },
  });
}
